/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { Button, ButtonType, Heading, Icon, Size } from '@vaisala/rockhopper-components';
import { useTranslation } from 'react-i18next';
import { ParameterConsumerProps } from './ParameterControls';
import ParameterRow from './ParameterRow';
import './ParameterCard.scss';
import InfoButton from './InfoButton';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { paramNameForRow } from '../../modules/calculator/configParser';
import React from 'react';

export type CardType = 'ambient' | 'humidity' | 'selector';

export interface ParameterCardProps extends ParameterConsumerProps {
  config: any;
  advancedMode: boolean;
  isSelectable: boolean;
  showControls: boolean;
  showResults: boolean;
  headers: string[];
  headerHelp: string;
  headerArrow: boolean;
  cardType: CardType;
  elevationMode: boolean;
  onToggleElevation: (forceElevation?: boolean) => void;
  setEditCondition: React.Dispatch<React.SetStateAction<'temperature' | 'pressure' | 'elevation' | null>>;
  editCondition: 'temperature' | 'pressure' | 'elevation' | null;
}

const ParameterCard = (props: ParameterCardProps) => {
  const { t } = useTranslation();
  const {
    config,
    advancedMode,
    selectedRowIndex,
    isSelectable,
    showControls,
    headers,
    cardType,
    headerHelp,
    headerArrow,
    onOptionsOpen,
    onSelectRow,
    elevationMode,
    onToggleElevation,
    setEditCondition,
    editCondition,
  } = props;

  const [rowsPerMode, setRowsPerMode] = React.useState((config.rows as any[]).filter((row) => advancedMode || !row.isAdvanced));
  const regularParameters = rowsPerMode.filter((row: any) => !row.additional_parameter).length;

  React.useEffect(() => {
    const removeDuplicatePressure = elevationMode
      ? (config.rows as any[]).filter((row) => advancedMode || !row.isAdvanced).filter((obj: any) => obj.labelKey !== 'param.pressure')
      : (config.rows as any[]).filter((row) => advancedMode || !row.isAdvanced).filter((obj: any) => obj.labelKey !== 'param.elevation');
    setRowsPerMode(removeDuplicatePressure);
  }, [config, elevationMode, advancedMode]);

  const renderHeader = (header: string, i: number, total: number) => (
    <div key={i} className={`col-${i} header-wrap ${total === 1 ? 'single' : ''}`}>
      {header && header.length > 0 && <Heading level={4}>{t(header)}</Heading>}
      {headerHelp && i === 0 && <InfoButton tooltip={t(headerHelp)} hPos={cardType === 'selector' ? 'right' : 'center'} vPos="bottom" />}
      {headerArrow && i === 2 && <Icon className="arrow-icon" size={Size.L} name={VaiIcon.ArrowRight} />}
      {cardType === 'selector' && i === 2 && (
        <div className="button-wrap change-button">
          <Button
            className="info"
            buttonType={ButtonType.Link}
            buttonSize={Size.S}
            onClick={() =>
              onOptionsOpen(t('card.from'), {
                options: rowsPerMode.map((row: any) => t(row.labelKey)),
                selectedIndex: selectedRowIndex,
                onSelect: (ind: number) => onSelectRow(ind, paramNameForRow(config.rows[ind])),
              })
            }
          >
            {t('card.changeParameter')}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`parameter-card ${cardType} ${editCondition ?? ''}`}>
      <div className="headers">{headers.map((header, i) => renderHeader(header, i, headers.length))}</div>

      {cardType === 'selector' && (
        <ParameterRow
          toleranceEnabled={config.toleranceEnabled}
          {...props}
          rowIndex={selectedRowIndex}
          config={config.rows[selectedRowIndex]}
          isSelectable={isSelectable}
          isActive={false}
          showControls={showControls}
          elevationMode={elevationMode}
          onToggleElevation={onToggleElevation}
          editCondition={editCondition}
          setEditCondition={setEditCondition}
        />
      )}
      {cardType !== 'selector' && (
        <>
          {rowsPerMode
            .filter((row: any) => !row.additional_parameter)
            .map((row: any, i: number) => (
              <React.Fragment key={i}>
                {!row.hidden && !row.additional_parameter && (
                  <ParameterRow
                    key={i}
                    toleranceEnabled={config.toleranceEnabled}
                    {...props}
                    rowIndex={i}
                    config={row}
                    isSelectable={isSelectable}
                    isActive={i === selectedRowIndex}
                    showControls={showControls && (i === selectedRowIndex || !isSelectable)}
                    editCondition={editCondition}
                    setEditCondition={setEditCondition}
                  />
                )}
              </React.Fragment>
            ))}
          {/* Check if at least one row has additional_parameter */}
          {rowsPerMode.some((row: any) => row.additional_parameter) && (
            <>
              <Heading level={4} className="additional-heading">
                {t('card.additional')}
              </Heading>

              {rowsPerMode
                .filter((row: any) => row.additional_parameter)
                .map((row: any, i: number) => (
                  <React.Fragment key={regularParameters + i}>
                    <ParameterRow
                      key={regularParameters + i}
                      toleranceEnabled={config.toleranceEnabled}
                      {...props}
                      rowIndex={regularParameters + i}
                      config={row}
                      isSelectable={isSelectable}
                      isActive={regularParameters + i === selectedRowIndex}
                      showControls={showControls && (regularParameters + i === selectedRowIndex || !isSelectable)}
                      editCondition={editCondition}
                      setEditCondition={setEditCondition}
                    />
                  </React.Fragment>
                ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ParameterCard;
